// Third Party
import React from 'react';
import classNames from 'classnames';

// Components
import LegacyBMOGIFPortfolioDropdown from './LegacyBMOGIFPortfolioDropdown';
import { Tooltip, ErrorTooltip } from '../../tooltips';
import { UnitInput } from '../../inputs';

// Utils
import Comparison from '@/types/comparison';
import { BMOi18n, GIF_SCHEME_TYPES } from '@/utils/bmo-gif/constants';
import { getReadOnlyTooltipMessage } from '@/utils/comparison';
import { getAge } from '@/utils/time';

type Props = {
  locale: string;
  title?: string;
  dark?: boolean;
  selectedScheme?: string;
  start?: number;
  end?: number;
  startMonth?: number;
  endMonth?: number;
  numYears?: number;
  term?: number;
  purchaseDate?: Date;
  setNumYears: (value?: number) => void;
  setStart: (value?: number) => void;
  setEnd: (value?: number) => void;
  setSchemeType: (value: string) => void;
  disabledInputs?: boolean;
  dob?: Date;
  readOnly?: boolean;
  comparison?: Comparison;
  setMonthEnd: (month?: number) => void;
  setMonthStart: (month?: number) => void;
  showMonths?: boolean;
  inValidSubsequentDeposit?: boolean;
};

const BMOGIFYearAgeInputs = ({ ...props }: Props) => {
  const {
    locale,
    title,
    start,
    end,
    term,
    numYears,
    selectedScheme,
    showMonths,
    startMonth,
    endMonth,
    dob,
    disabledInputs,
    dark,
    readOnly,
    comparison,
    purchaseDate,
    setNumYears,
    setStart,
    setSchemeType,
    setEnd,
    setMonthEnd,
    setMonthStart,
    inValidSubsequentDeposit,
  } = props;

  const schemeOptions = [
    {
      value: GIF_SCHEME_TYPES.YEAR,
      label: BMOi18n[locale].IN_YEARS,
    },
    {
      value: GIF_SCHEME_TYPES.AGE,
      label: BMOi18n[locale].IN_AGE,
    },
  ];

  const tooltipMessage = readOnly ? getReadOnlyTooltipMessage(comparison) : BMOi18n[locale].INVALID_DEPOSIT_AMOUNT;

  const renderYearAgeInputs = () => {
    const baseAge = !!dob && getAge(dob, undefined, purchaseDate);
    if (selectedScheme === GIF_SCHEME_TYPES.YEAR) {
      return (
        <>
          <div className="years d-table-row">
            <div className={classNames('input-title d-table-cell', { disabled: disabledInputs })}>
              <span>{BMOi18n[locale].NUMBER_OF_YEARS}</span>
            </div>

            <Tooltip className="d-table-cell" message={tooltipMessage} disabled={!disabledInputs}>
              <span>
                <UnitInput
                  type="number"
                  dark={dark}
                  min={1}
                  value={numYears || ''}
                  onValueChange={(value: string) => setNumYears(value ? Number(value) : undefined)}
                  disabled={disabledInputs}
                />
              </span>
            </Tooltip>

            <div className={classNames('input-title d-table-cell', { disabled: disabledInputs })}>
              <span>{BMOi18n[locale].START_YEAR}</span>
            </div>

            <Tooltip className="d-table-cell" message={tooltipMessage} disabled={!disabledInputs}>
              <span>
                <UnitInput
                  type="number"
                  dark={dark}
                  min={1}
                  value={start || ''}
                  onValueChange={(value: string) => setStart(value ? Number(value) : undefined)}
                  disabled={disabledInputs}
                />
                {inValidSubsequentDeposit && <ErrorTooltip message={BMOi18n[locale].INVALID_DEPOSIT_AGE} />}
                {!!term && start !== undefined && start > term && <ErrorTooltip message={BMOi18n[locale].INVALID_START_YEAR} />}
              </span>
            </Tooltip>
          </div>

          {showMonths && (
            <div className="months d-table-row">
              <div className={classNames('input-title d-table-cell', { disabled: disabledInputs })}>
                <span>{BMOi18n[locale].START_MONTH}</span>
              </div>

              <Tooltip className="d-table-cell" message={tooltipMessage} disabled={!disabledInputs}>
                <span>
                  <UnitInput
                    type="number"
                    dark={dark}
                    min={1}
                    max={12}
                    defaultValue="1"
                    value={startMonth}
                    onValueChange={(value: string) => setMonthStart(value ? Number(value) : undefined)}
                    disabled={disabledInputs}
                  />

                  {(startMonth || 1) > (endMonth || 12) && <ErrorTooltip message={BMOi18n[locale].INVALID_START_MONTH} />}
                </span>
              </Tooltip>

              <div className={classNames('input-title d-table-cell', { disabled: disabledInputs })}>
                <span>{BMOi18n[locale].END_MONTH}</span>
              </div>

              <Tooltip className="d-table-cell" message={tooltipMessage} disabled={!disabledInputs}>
                <span>
                  <UnitInput
                    type="number"
                    dark={dark}
                    min={1}
                    max={12}
                    defaultValue="12"
                    value={endMonth}
                    onValueChange={(value: string) => setMonthEnd(value ? Number(value) : undefined)}
                    disabled={disabledInputs}
                  />
                  {(endMonth || 12) > 12 && <ErrorTooltip message={BMOi18n[locale].INVALID_END_MONTH} />}
                </span>
              </Tooltip>
            </div>
          )}
        </>
      );
    }

    return (
      <div className="d-table-row">
        <div className={classNames('input-title d-table-cell', { disabled: disabledInputs })}>
          <span>{BMOi18n[locale].FROM_AGE}</span>
        </div>

        <Tooltip className="d-table-cell" message={tooltipMessage} disabled={!disabledInputs}>
          <span>
            <UnitInput
              type="number"
              dark={dark}
              min={0}
              value={start}
              onValueChange={(value: string) => setStart(value ? Number(value) : undefined)}
              disabled={disabledInputs}
            />

            {!!baseAge && start !== undefined && start < baseAge && <ErrorTooltip message={BMOi18n[locale].INVALID_FROM_AGE} />}
          </span>
        </Tooltip>

        <div className={classNames('input-title d-table-cell', { disabled: disabledInputs })}>
          <span>{BMOi18n[locale].TO_AGE}</span>
        </div>

        <Tooltip className="d-table-cell" message={tooltipMessage} disabled={!disabledInputs}>
          <span>
            <UnitInput
              type="number"
              dark={dark}
              min={0}
              value={end || ''}
              onValueChange={(value: string) => setEnd(Number(value))}
              disabled={disabledInputs || !start}
            />
            {inValidSubsequentDeposit && <ErrorTooltip message={BMOi18n[locale].INVALID_DEPOSIT_AGE} />}
            {!!start && !!end && end <= start && <ErrorTooltip message={BMOi18n[locale].INVALID_TO_AGE} />}
          </span>
        </Tooltip>
      </div>
    );
  };

  return (
    <div className="bmo-gif-year-age-inputs">
      <div className="d-flex align-items-center">
        {title && (
          <div className={classNames('input-title', { disabled: disabledInputs })}>
            <span>{title}</span>
          </div>
        )}

        <LegacyBMOGIFPortfolioDropdown
          className="scheme"
          dark={dark}
          selected={selectedScheme}
          onChange={setSchemeType}
          options={schemeOptions}
          disabled={disabledInputs}
          tooltipMessage={tooltipMessage}
          tooltipDisabled={!disabledInputs}
        />
      </div>

      <div className="year-age-inputs d-table">{renderYearAgeInputs()}</div>
    </div>
  );
};

export default BMOGIFYearAgeInputs;
